import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Reveal } from '@/components/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { TryItNowWidget } from '@/components/TryItNowWidget';
import {
  Sparkles,
  Languages,
  Zap,
  CheckCircle2,
  ArrowRight,
  Wand2,
  Clock,
  TrendingUp,
  Crown,
  Loader2,
  Copy,
  Check,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';


const faqs = [
  {
    q: 'Is the AI real estate description generator really free?',
    a: 'Yes. The free real estate description generator on this page runs with no signup and no credit card — a few generations per day per visitor. For full multi-language descriptions, premium prose, and unlimited listings, upgrade to a paid plan starting at $15 for a single property kit.',
  },
  {
    q: 'How does ListBright generate property descriptions?',
    a: 'You enter a few facts about the property (type, city, beds, baths, size, key features). Our AI uses vision and language models to write a polished, marketplace-ready property description tailored to the listing — in 28+ languages simultaneously.',
  },
  {
    q: 'Can I use AI to write MLS listing descriptions?',
    a: 'Yes. AI-written MLS descriptions are allowed as long as the facts are accurate and the copy follows Fair Housing rules. ListBright writes factual, feature-led copy that avoids protected-class language, and every description is fully editable before you publish it.',
  },
  {
    q: 'What is the best AI for real estate listing descriptions?',
    a: 'General chatbots can write listing copy, but they need re-prompting for each platform. A purpose-built real estate AI like ListBright already knows MLS length limits, Facebook and Instagram formatting, and multi-language output, so you get every format in one click instead of one prompt at a time.',
  },
  {
    q: 'How long should a real estate listing description be?',
    a: 'Aim for 150–250 words for MLS, 80–120 words for Facebook Marketplace, and 1–2 short lines plus hashtags for Instagram. The free generator returns an MLS-length description; paid plans generate every format at the right length automatically.',
  },
  {
    q: 'What marketplaces are supported?',
    a: 'Facebook, Instagram, Instagram Stories, and a General Marketplace template — plus a Premium Description for brochures, MLS, and websites.',
  },
  {
    q: 'Can I edit the description?',
    a: 'Absolutely. Every generation is fully editable. You can also save default tone, length, and focus features so future listings match your style.',
  },
  {
    q: 'Does it work for rentals and commercial property?',
    a: 'Yes. Choose sale or rent, and pick any property type — apartment, house, villa, studio, condo, townhouse, land, or commercial. The AI adjusts tone, highlights, and calls to action to the property category and market.',
  },
];

const steps = [
  {
    title: 'Enter the property facts',
    body: 'Property type, city, bedrooms, bathrooms, size in sq ft or m², and a few highlights. No photos or signup needed.',
  },
  {
    title: 'Generate the description',
    body: 'The AI writes an MLS-ready listing description with a headline and feature bullets in about ten seconds.',
  },
  {
    title: 'Copy, edit, publish',
    body: 'Copy the copy straight into your MLS, Facebook Marketplace, or brochure — or build the full listing kit with photos, flyer, and microsite.',
  },
];


interface DemoResult {
  title: string;
  description: string;
  bullets: string[];
}

export default function AiRealEstateDescriptions() {
  const navigate = useNavigate();
  const [type, setType] = useState('Apartment');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [area, setArea] = useState('');
  const [areaUnit, setAreaUnit] = useState<'sqm' | 'sqft'>('sqft');
  const [listingType, setListingType] = useState<'sale' | 'rent'>('sale');
  const [city, setCity] = useState('');
  const [features, setFeatures] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DemoResult | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('demo-generate-description', {
        body: { type, city, bedrooms: beds, bathrooms: baths, area, areaUnit, listingType, features },
      });

      // Non-2xx responses surface as FunctionsHttpError — read the JSON body for
      // the friendly message (rate limits, capacity, etc.).
      if (error) {
        let message = 'Something went wrong. Please try again.';
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = await ctx.json();
            if (body?.error) message = body.error as string;
          } catch { /* keep default */ }
        }
        toast.error(message);
        return;
      }

      if (data?.error) {
        toast.error(data.error as string);
        return;
      }

      setResult({
        title: data?.title ?? '',
        description: data?.description ?? '',
        bullets: Array.isArray(data?.bullets) ? (data.bullets as string[]) : [],
      });
      setCopied(false);
    } catch (err) {
      console.error('Free description generation failed:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    const text = [
      result.title,
      '',
      result.description,
      result.bullets.length ? `\n${result.bullets.map((b) => `• ${b}`).join('\n')}` : '',
    ].join('\n').trim();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Description copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please select the text manually.');
    }
  };


  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Free AI Real Estate Description Generator',
      alternateName: [
        'Real estate description generator',
        'AI listing description generator',
        'Property description generator',
      ],
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://listbright.ai/ai-real-estate-descriptions',
      isAccessibleForFree: true,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      featureList: [
        'Free AI listing description generator, no signup',
        'MLS-ready property descriptions',
        'Facebook and Instagram listing copy',
        '28+ language output on paid plans',
        'Fully editable results',
      ],
      publisher: { '@type': 'Organization', name: 'ListBright', url: 'https://listbright.ai' },
      description:
        'Free AI real estate description generator. Write polished, MLS-ready property listing descriptions for Facebook, Instagram, brochures, and websites in seconds.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to write a real estate listing description with AI',
      totalTime: 'PT2M',
      estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
      step: steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.title,
        text: s.body,
        url: `https://listbright.ai/ai-real-estate-descriptions#generator`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="Free AI Real Estate Description Generator | ListBright"
        description="Free AI real estate description generator — write MLS-ready property listing descriptions in seconds. No signup, fully editable, works for sales and rentals."
        canonical="https://listbright.ai/ai-real-estate-descriptions"
        breadcrumbs={[{ label: 'Free AI Real Estate Description Generator', path: '/ai-real-estate-descriptions' }]}
        jsonLd={jsonLd}
      />


      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-border/40 py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-6rem] right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-5xl mx-auto px-4 relative">
          <div className="text-center space-y-6">
            <Badge variant="secondary" className="bg-primary/10 text-primary border-0 px-4 py-1.5 animate-fade-in">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Free — no signup
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] animate-fade-up">
              Free AI Real Estate{' '}
              <span className="text-gradient">Description Generator</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: '80ms' }}>
              Write MLS-ready property listing descriptions in seconds. Enter a few facts about the
              home and this free real estate AI writes the headline, description, and feature
              bullets — no signup, fully editable, ready for MLS, Facebook Marketplace, or your
              brochure.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground pt-2">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Free, no credit card</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Sales &amp; rentals</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Fully editable</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> 28+ languages on paid plans</span>
            </div>

          </div>
        </div>
      </section>

      {/* ── Generator widget ── */}
      <section id="generator" className="py-12 md:py-16 -mt-4">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-primary/15 via-primary/5 to-transparent blur-3xl rounded-[2.5rem] opacity-70 pointer-events-none" />
            <Card className="relative border-2 border-primary/10 shadow-elevated">
              <CardContent className="p-6 md:p-10">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-display font-bold mb-2">Try the Free Generator</h2>
                  <p className="text-muted-foreground">
                    Tell us about the property — we'll write the description.
                  </p>
                </div>
                <form onSubmit={handleGenerate} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="type">Property Type</Label>
                      <select
                        id="type"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {['Apartment', 'House', 'Villa', 'Studio', 'Condo', 'Townhouse', 'Land', 'Commercial'].map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City / Area</Label>
                      <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Austin, TX" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="listingType">For</Label>
                      <select
                        id="listingType"
                        value={listingType}
                        onChange={(e) => setListingType(e.target.value as 'sale' | 'rent')}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="sale">Sale</option>
                        <option value="rent">Rent</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="beds">Bedrooms</Label>
                      <Input id="beds" type="number" min="0" value={beds} onChange={(e) => setBeds(e.target.value)} placeholder="3" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="baths">Bathrooms</Label>
                      <Input id="baths" type="number" min="0" value={baths} onChange={(e) => setBaths(e.target.value)} placeholder="2" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="area">Size</Label>
                      <div className="flex gap-2">
                        <Input id="area" type="number" min="0" value={area} onChange={(e) => setArea(e.target.value)} placeholder="1450" />
                        <select
                          aria-label="Area unit"
                          value={areaUnit}
                          onChange={(e) => setAreaUnit(e.target.value as 'sqm' | 'sqft')}
                          className="flex h-10 rounded-md border border-input bg-background px-2 text-sm"
                        >
                          <option value="sqft">sq ft</option>
                          <option value="sqm">m²</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="features">Key Features & Highlights</Label>
                    <Textarea
                      id="features"
                      value={features}
                      onChange={(e) => setFeatures(e.target.value)}
                      placeholder="Renovated 2024, quartz kitchen, fenced backyard, walk to schools…"
                      rows={3}
                    />
                  </div>
                  <Button type="submit" size="lg" disabled={loading} className="w-full text-base group shadow-lg">
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Writing your description…
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Generate Description Free
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    No signup, no credit card. A few free generations per day.
                  </p>
                </form>

                {result && (
                  <div className="mt-8 pt-8 border-t border-border/60 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-xl font-display font-bold leading-snug">{result.title}</h3>
                      <Button type="button" variant="outline" size="sm" onClick={copyResult} className="shrink-0">
                        {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                    <div className="space-y-3 text-[15px] leading-relaxed text-muted-foreground whitespace-pre-line">
                      {result.description}
                    </div>
                    {result.bullets.length > 0 && (
                      <ul className="grid sm:grid-cols-2 gap-2 pt-1">
                        {result.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="rounded-xl bg-muted/50 p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                      <p className="text-sm text-muted-foreground">
                        Want this in 28 languages, plus enhanced photos, a flyer and a microsite?
                      </p>
                      <Button size="sm" onClick={() => navigate('/auth')} className="shrink-0">
                        Build the full listing
                        <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Button>
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-16 lg:py-20 border-t border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">
              How to write a listing description with AI
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Three steps, about two minutes, no account required.
            </p>
          </Reveal>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <li className="h-full rounded-2xl border border-border/60 bg-card p-6 space-y-3">
                  <span className="inline-flex w-9 h-9 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold">
                    {i + 1}
                  </span>
                  <h3 className="font-semibold text-lg">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Editorial / keyword depth ── */}
      <section className="py-16 lg:py-20 border-t border-border/40">
        <div className="container max-w-3xl mx-auto px-4 space-y-10">
          <Reveal className="space-y-4">
            <h2 className="text-3xl md:text-4xl font-display font-bold">
              Why agents use a real estate description generator
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Listing copy is the part of the job that quietly eats the most time. A single property
              needs an MLS description, a shorter Facebook Marketplace version, an Instagram caption,
              and often a brochure paragraph — each with different length and tone. Writing all four
              by hand takes 30–45 minutes per listing. A purpose-built AI listing description
              generator produces every version from the same set of facts, so you edit instead of
              starting from a blank page.
            </p>
          </Reveal>

          <Reveal className="space-y-4">
            <h3 className="text-xl md:text-2xl font-display font-bold">
              What makes a property description convert
            </h3>
            <ul className="space-y-2 text-muted-foreground">
              {[
                'Lead with the single strongest feature, not the property type.',
                'Use concrete, verifiable details — renovation year, materials, walk times.',
                'Keep MLS copy to 150–250 words and front-load the first two sentences.',
                'Never describe the buyer or neighbourhood demographics — stay Fair Housing safe.',
                'Close with a clear next step: book a viewing, request the floor plan, see the video.',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-1 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="space-y-4">
            <h3 className="text-xl md:text-2xl font-display font-bold">
              Works for every listing type
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              The generator handles apartments, houses, villas, studios, condos, townhouses, land,
              and commercial units, for sale or for rent, in sq ft or m². It adapts the tone to the
              market — a Texas single-family listing reads differently to a downtown rental or a
              waterfront villa — and paid plans generate the same listing in 28+ languages at once.
            </p>
          </Reveal>

          <Reveal className="space-y-3">
            <h3 className="text-xl md:text-2xl font-display font-bold">Keep reading</h3>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Link className="rounded-xl border border-border/60 p-4 hover:border-primary/40 transition-colors" to="/real-estate-description-examples">
                Real estate description examples →
              </Link>
              <Link className="rounded-xl border border-border/60 p-4 hover:border-primary/40 transition-colors" to="/how-to-write-a-real-estate-listing-description">
                How to write a listing description →
              </Link>
              <Link className="rounded-xl border border-border/60 p-4 hover:border-primary/40 transition-colors" to="/best-ai-for-real-estate">
                Best AI tools for real estate agents →
              </Link>
              <Link className="rounded-xl border border-border/60 p-4 hover:border-primary/40 transition-colors" to="/ai-virtual-staging">
                Free AI virtual staging →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>



      {/* ── Photo enhancement teaser ── */}
      <section className="py-16 lg:py-20 border-y border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <Badge variant="outline" className="mb-4">Bonus</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">
              Great descriptions deserve great photos
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Try our AI photo enhancer free — declutter, brighten, and stage any room in seconds.
            </p>
          </Reveal>
          <Reveal delay={80}>
            <TryItNowWidget onGetStarted={() => navigate('/auth')} />
          </Reveal>
        </div>
      </section>

      {/* ── Why upgrade ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-14">
            <Badge className="mb-4"><Crown className="w-3.5 h-3.5 mr-1.5" /> Upgrade for the full kit</Badge>
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-3">
              Get descriptions that actually sell
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              The free generator gets you started. Upgrade for premium prose, every marketplace
              format, and 28-language output — generated in parallel, in one click.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Languages, title: '28+ Languages', desc: 'Every description generated in 28 languages simultaneously and saved.' },
              { icon: TrendingUp, title: 'Premium Prose', desc: 'Research-backed long-form copy that boosts click-through by up to 45%.' },
              { icon: Zap, title: '5 Output Formats', desc: 'Facebook, Instagram, Stories, General Marketplace, and Premium Description — all at once.' },
              { icon: Clock, title: 'Done in 10 Minutes', desc: 'A complete listing kit — descriptions, enhanced photos, video — ready to post.' },
              { icon: Sparkles, title: 'Photo Enhancement', desc: '10 professional rules applied to every photo. Virtual staging included.' },
              { icon: Wand2, title: 'Saved Preferences', desc: 'Your tone, length, and focus features apply automatically to every new listing.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-6 space-y-3 group">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center transition-colors group-hover:bg-primary/20">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center space-y-4">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="text-base shadow-lg">
                <Link to="/single-property-kit">
                  Get Single Property Kit – $15
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/pricing">See All Plans</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Trusted by agents in 30+ countries. Cancel anytime.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 lg:py-28 bg-muted/30">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-display font-bold">Frequently Asked Questions</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <div className="text-center mt-12">
            <Button asChild size="lg" className="group shadow-lg">
              <Link to="/ai-real-estate-descriptions#generator">
                Try the Free Generator
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
